import '@/App.css';
import '@/index.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider, useApp } from '@/context/AppContext';
import { Toaster } from '@/components/ui/sonner';

import LoginPage from '@/pages/LoginPage';
import MMGDashboard from '@/pages/MMGDashboard';
import MMGPlaceholder from '@/pages/MMGPlaceholder';
import MMGQuickPrescription from '@/pages/MMGQuickPrescription';
import MMGCommunicationHub from '@/pages/MMGCommunicationHub';
import MMGFseAccess from '@/pages/MMGFseAccess';
import MMGRequestsHistory from '@/pages/MMGRequestsHistory';
import ProgrammatoMMG from '@/pages/ProgrammatoMMG';
import NewADICreationWizard from '@/pages/NewADICreationWizard';
import MMGPatientList from '@/pages/MMGPatientList';
import MMGPatientDossier from '@/pages/MMGPatientDossier';
import SegreteriaDashboard from '@/pages/SegreteriaDashboard';
import CotControlRoom from '@/pages/CotControlRoom';
import CentraleAllarmi from '@/pages/CentraleAllarmi';
import DimissioniProtette from '@/pages/DimissioniProtette';
import HubTeleconsulti from '@/pages/HubTeleconsulti';
import DettaglioRichiesta from '@/pages/DettaglioRichiesta';
import MedicoInbox from '@/pages/MedicoInbox';
import MedicoValutazione from '@/pages/MedicoValutazione';
import AgendaGiorno from '@/pages/AgendaGiorno';
import CaseManagerDashboard from '@/pages/CaseManagerDashboard';
import CaseManagerPatientDetail from '@/pages/CaseManagerPatientDetail';
import CaseManagerPAIBuilder from '@/pages/CaseManagerPAIBuilder';
import CaseManagerPAIHub from '@/pages/CaseManagerPAIHub';
import CaseManagerPlaceholder from '@/pages/CaseManagerPlaceholder';
import CronologiaPazienti from '@/pages/CronologiaPazienti';
import ComunicazioniCaseManager from '@/pages/ComunicazioniCaseManager';
import DiarioClinico from '@/pages/DiarioClinico';
import ArchivioPazientiMedico from '@/pages/ArchivioPazientiMedico';
import FascicoloClinicoMedico from '@/pages/FascicoloClinicoMedico';
import BozzeValutazioni from '@/pages/BozzeValutazioni';
import RivalutazioneUvmdDecisionRoom from '@/pages/RivalutazioneUvmdDecisionRoom';
import ProviderTrackerDashboard from '@/pages/ProviderTrackerDashboard';
import OperatoreDashboard from '@/pages/OperatoreDashboard';
import OperatoreArchivio from '@/pages/OperatoreArchivio';
import HealthWorkerPatientFile from '@/pages/HealthWorkerPatientFile';
import HealthWorkerCommunications from '@/pages/HealthWorkerCommunications';
import PazientePortale from '@/pages/PazientePortale';
import PatientDashboard from '@/pages/PatientDashboard';
import PatientPlaceholder from '@/pages/PatientPlaceholder';
import PatientCarePlan from '@/pages/PatientCarePlan';
import PatientAgenda from '@/pages/PatientAgenda';
import PatientVitalsMonitor from '@/pages/PatientVitalsMonitor';
import PatientCommunications from '@/pages/PatientCommunications';
import PatientPrivacy from '@/pages/PatientPrivacy';
import PatientArchiveWrapper from '@/pages/PatientArchiveWrapper';
import ASLDashboard from '@/pages/ASLDashboard';
import ASLFinance from '@/pages/ASLFinance';
import ASLPerformance from '@/pages/ASLPerformance';
import ASLValore from '@/pages/ASLValore';
import ASLSimulazione from '@/pages/ASLSimulazione';
import ASLBudgetSalute from '@/pages/ASLBudgetSalute';
import ASLAuditDettaglio from '@/pages/ASLAuditDettaglio';
import PuaDashboard from '@/pages/PuaDashboard';
import PuaNuovaRichiesta from '@/pages/PuaNuovaRichiesta';
import OspedaleDashboard from '@/pages/OspedaleDashboard';
import OspedaleRichiesta from '@/pages/OspedaleRichiesta';
import OspedaleConcordata from '@/pages/OspedaleConcordata';
import OspedaleFollowup from '@/pages/OspedaleFollowup';

function ProtectedRoute({ children }) {
  const { isAuthenticated, currentRole } = useApp();
  if (!isAuthenticated || !currentRole) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LoginPage />} />

      {/* MMG */}
      <Route path="/mmg" element={<ProtectedRoute><MMGDashboard /></ProtectedRoute>} />
      <Route path="/mmg/nuova-richiesta" element={<ProtectedRoute><NewADICreationWizard /></ProtectedRoute>} />
      <Route path="/mmg/pazienti" element={<ProtectedRoute><MMGPatientList /></ProtectedRoute>} />
      <Route path="/mmg/pazienti/:id" element={<ProtectedRoute><MMGPatientDossier /></ProtectedRoute>} />
      <Route path="/mmg/prescrizioni" element={<ProtectedRoute><MMGQuickPrescription /></ProtectedRoute>} />
      <Route path="/mmg/comunicazioni" element={<ProtectedRoute><MMGCommunicationHub /></ProtectedRoute>} />
      <Route path="/mmg/programmato" element={<ProtectedRoute><ProgrammatoMMG /></ProtectedRoute>} />
      <Route path="/mmg/richieste" element={<ProtectedRoute><MMGRequestsHistory /></ProtectedRoute>} />

      {/* Segreteria / COT */}
      <Route path="/segreteria" element={<ProtectedRoute><CotControlRoom /></ProtectedRoute>} />
      <Route path="/segreteria/triage" element={<ProtectedRoute><SegreteriaDashboard /></ProtectedRoute>} />
      <Route path="/segreteria/richiesta/:id" element={<ProtectedRoute><DettaglioRichiesta /></ProtectedRoute>} />
      <Route path="/segreteria/allarmi" element={<ProtectedRoute><CentraleAllarmi /></ProtectedRoute>} />
      <Route path="/segreteria/dimissioni" element={<ProtectedRoute><DimissioniProtette /></ProtectedRoute>} />
      <Route path="/segreteria/teleconsulti" element={<ProtectedRoute><HubTeleconsulti /></ProtectedRoute>} />

      {/* Medico UVMD */}
      <Route path="/medico" element={<ProtectedRoute><AgendaGiorno /></ProtectedRoute>} />
      <Route path="/medico/bozze" element={<ProtectedRoute><BozzeValutazioni /></ProtectedRoute>} />
      <Route path="/medico/inbox" element={<ProtectedRoute><MedicoInbox /></ProtectedRoute>} />
      <Route path="/medico/valutazione/:id" element={<ProtectedRoute><MedicoValutazione /></ProtectedRoute>} />
      <Route path="/medico/pazienti" element={<ProtectedRoute><ArchivioPazientiMedico /></ProtectedRoute>} />
      <Route path="/medico/pazienti/:id" element={<ProtectedRoute><FascicoloClinicoMedico /></ProtectedRoute>} />
      <Route path="/medico/rivalutazione-clinica/:id" element={<ProtectedRoute><RivalutazioneUvmdDecisionRoom /></ProtectedRoute>} />

      {/* Case Manager */}
      <Route path="/case-manager" element={<ProtectedRoute><CaseManagerDashboard /></ProtectedRoute>} />
      <Route path="/case-manager/paziente/:id" element={<ProtectedRoute><CaseManagerPatientDetail /></ProtectedRoute>} />
      <Route path="/case-manager/paziente/:id/pai-builder" element={<ProtectedRoute><CaseManagerPAIBuilder /></ProtectedRoute>} />
      <Route path="/case-manager/cronologia" element={<ProtectedRoute><CronologiaPazienti /></ProtectedRoute>} />
      <Route path="/case-manager/pai-builder" element={<ProtectedRoute><CaseManagerPAIHub /></ProtectedRoute>} />
      <Route path="/case-manager/logistica" element={<ProtectedRoute><ProviderTrackerDashboard /></ProtectedRoute>} />
      <Route path="/case-manager/diario" element={<ProtectedRoute><DiarioClinico /></ProtectedRoute>} />
      <Route path="/case-manager/comunicazioni" element={<ProtectedRoute><ComunicazioniCaseManager /></ProtectedRoute>} />

      {/* Operatore Sanitario */}
      <Route path="/operatore" element={<ProtectedRoute><OperatoreDashboard /></ProtectedRoute>} />
      <Route path="/operatore/visita/:id" element={<ProtectedRoute><HealthWorkerPatientFile /></ProtectedRoute>} />
      <Route path="/operatore/archivio" element={<ProtectedRoute><OperatoreArchivio /></ProtectedRoute>} />
      <Route path="/operatore/comunicazioni" element={<ProtectedRoute><HealthWorkerCommunications /></ProtectedRoute>} />

      {/* Paziente */}
      <Route path="/paziente" element={<ProtectedRoute><PatientDashboard /></ProtectedRoute>} />
      <Route path="/paziente/pai" element={<ProtectedRoute><PatientCarePlan /></ProtectedRoute>} />
      <Route path="/paziente/agenda" element={<ProtectedRoute><PatientAgenda /></ProtectedRoute>} />
      <Route path="/paziente/telemonitoraggio" element={<ProtectedRoute><PatientVitalsMonitor /></ProtectedRoute>} />
      <Route path="/paziente/comunicazioni" element={<ProtectedRoute><PatientCommunications /></ProtectedRoute>} />
      <Route path="/paziente/archivio" element={<ProtectedRoute><PatientArchiveWrapper /></ProtectedRoute>} />
      <Route path="/paziente/privacy" element={<ProtectedRoute><PatientPrivacy /></ProtectedRoute>} />

      {/* ASL */}
      <Route path="/asl" element={<ProtectedRoute><ASLDashboard /></ProtectedRoute>} />
      <Route path="/asl/finance" element={<ProtectedRoute><ASLFinance /></ProtectedRoute>} />
      <Route path="/asl/performance" element={<ProtectedRoute><ASLPerformance /></ProtectedRoute>} />
      <Route path="/asl/valore" element={<ProtectedRoute><ASLValore /></ProtectedRoute>} />
      <Route path="/asl/simulazione" element={<ProtectedRoute><ASLSimulazione /></ProtectedRoute>} />
      <Route path="/asl/budget-salute" element={<ProtectedRoute><ASLBudgetSalute /></ProtectedRoute>} />
      <Route path="/asl/budget-salute/audit/:id" element={<ProtectedRoute><ASLAuditDettaglio /></ProtectedRoute>} />

      {/* PUA */}
      <Route path="/pua" element={<ProtectedRoute><PuaDashboard /></ProtectedRoute>} />
      <Route path="/pua/nuova-richiesta" element={<ProtectedRoute><PuaNuovaRichiesta /></ProtectedRoute>} />

      {/* Ospedale */}
      <Route path="/ospedale" element={<ProtectedRoute><OspedaleDashboard /></ProtectedRoute>} />
      <Route path="/ospedale/richiesta" element={<ProtectedRoute><OspedaleRichiesta /></ProtectedRoute>} />
      <Route path="/ospedale/concordata" element={<ProtectedRoute><OspedaleConcordata /></ProtectedRoute>} />
      <Route path="/ospedale/followup" element={<ProtectedRoute><OspedaleFollowup /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </AppProvider>
  );
}

export default App;
